/**
 * Offline Storage & Sync Queue
 * Uses localStorage to cache critical data and queue mutations for later sync.
 */

const SYNC_QUEUE_KEY = 'insaneforce_sync_queue';
const CACHE_PREFIX = 'insaneforce_cache_';

// ── Types ──────────────────────────────────────────────────────────────

export interface SyncQueueItem {
  id: string;
  table: string;
  action: 'insert' | 'update' | 'upsert' | 'delete';
  data: Record<string, unknown>;
  matchColumns?: string[];
  timestamp: number;
  /** Who created this queue item — sync only runs for the current auth user.
   *  Optional for backward compat with items queued before this field existed. */
  userId?: string;
  /** How many times we've tried to sync this item (default 0) */
  attempts?: number;
  /** Last error message if any */
  lastError?: string;
  /** Last attempt timestamp */
  lastAttemptAt?: number;
  /** Set to true when the failure looks like an expired auth token. */
  needsReAuth?: boolean;
}

/** Photo queued for upload while offline. Stored as base64 in localStorage. */
export interface PhotoQueueItem {
  id: string;
  userId: string;
  profileName: string;
  photoMonth: string; // YYYY-MM
  poseType: string;   // front | left | right | back
  base64: string;     // raw base64 (no data: prefix)
  contentType: string;
  ext: string;
  timestamp: number;
  attempts?: number;
  lastError?: string;
  lastAttemptAt?: number;
  needsReAuth?: boolean;
}

/** Items that have failed too many times and we've given up on. */
const DEAD_LETTER_KEY = 'insaneforce_dead_letter';
const PHOTO_QUEUE_KEY = 'insaneforce_photo_queue';
const PHOTO_DEAD_KEY = 'insaneforce_photo_dead';
const LAST_SYNC_KEY = 'insaneforce_last_sync';
/** Max attempts before moving to dead letter (8). */
export const MAX_SYNC_ATTEMPTS = 8;
/** Max photos we will hold in localStorage before refusing new ones (size guard). */
export const MAX_QUEUED_PHOTOS = 8;

// ── Sync Queue ─────────────────────────────────────────────────────────

export function getSyncQueue(): SyncQueueItem[] {
  try {
    const raw = localStorage.getItem(SYNC_QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function addToSyncQueue(item: Omit<SyncQueueItem, 'id' | 'timestamp'>) {
  const queue = getSyncQueue();
  queue.push({
    ...item,
    id: crypto.randomUUID(),
    timestamp: Date.now(),
  });
  localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
}

export function removeFromSyncQueue(id: string) {
  const queue = getSyncQueue().filter(i => i.id !== id);
  localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
}

export function clearSyncQueue() {
  localStorage.removeItem(SYNC_QUEUE_KEY);
}

/** Update an item in the queue (used to record attempt and error). */
export function updateQueueItem(id: string, updates: Partial<SyncQueueItem>) {
  const queue = getSyncQueue();
  const idx = queue.findIndex(i => i.id === id);
  if (idx === -1) return;
  queue[idx] = { ...queue[idx], ...updates };
  localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
}

// ── Dead Letter Queue (items we gave up on) ────────────────────────────

export function getDeadLetter(): SyncQueueItem[] {
  try {
    const raw = localStorage.getItem(DEAD_LETTER_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function moveToDeadLetter(item: SyncQueueItem) {
  const dead = getDeadLetter();
  dead.push(item);
  localStorage.setItem(DEAD_LETTER_KEY, JSON.stringify(dead));
  removeFromSyncQueue(item.id);
}

export function clearDeadLetter() {
  localStorage.removeItem(DEAD_LETTER_KEY);
}

export function removeFromDeadLetter(id: string) {
  const dead = getDeadLetter().filter(i => i.id !== id);
  localStorage.setItem(DEAD_LETTER_KEY, JSON.stringify(dead));
}

/** Move a dead-letter item back to the active queue for one more retry. */
export function retryFromDeadLetter(id: string) {
  const dead = getDeadLetter();
  const item = dead.find(i => i.id === id);
  if (!item) return;
  removeFromDeadLetter(id);
  const queue = getSyncQueue();
  queue.push({ ...item, attempts: 0, lastError: undefined, lastAttemptAt: undefined });
  localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
}

// ── Photo Queue ────────────────────────────────────────────────────────

export function getPhotoQueue(): PhotoQueueItem[] {
  try {
    const raw = localStorage.getItem(PHOTO_QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

/** Returns true if added; false if the queue is full. */
export function addToPhotoQueue(item: Omit<PhotoQueueItem, 'id' | 'timestamp'>): boolean {
  const queue = getPhotoQueue();
  // Replace any existing item for same user+month+pose so the latest wins,
  // matching the "upload overwrites the slot" semantics of the photos UI.
  const filtered = queue.filter(i =>
    !(i.userId === item.userId && i.photoMonth === item.photoMonth && i.poseType === item.poseType)
  );
  if (filtered.length >= MAX_QUEUED_PHOTOS) return false;
  filtered.push({ ...item, id: crypto.randomUUID(), timestamp: Date.now() });
  try {
    localStorage.setItem(PHOTO_QUEUE_KEY, JSON.stringify(filtered));
    return true;
  } catch {
    // localStorage full
    return false;
  }
}

export function removeFromPhotoQueue(id: string) {
  const queue = getPhotoQueue().filter(i => i.id !== id);
  localStorage.setItem(PHOTO_QUEUE_KEY, JSON.stringify(queue));
}

export function updatePhotoQueueItem(id: string, updates: Partial<PhotoQueueItem>) {
  const queue = getPhotoQueue();
  const idx = queue.findIndex(i => i.id === id);
  if (idx === -1) return;
  queue[idx] = { ...queue[idx], ...updates };
  localStorage.setItem(PHOTO_QUEUE_KEY, JSON.stringify(queue));
}

export function getPhotoDeadLetter(): PhotoQueueItem[] {
  try {
    const raw = localStorage.getItem(PHOTO_DEAD_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function moveToPhotoDeadLetter(item: PhotoQueueItem) {
  const dead = getPhotoDeadLetter();
  dead.push(item);
  localStorage.setItem(PHOTO_DEAD_KEY, JSON.stringify(dead));
  removeFromPhotoQueue(item.id);
}

export function removeFromPhotoDeadLetter(id: string) {
  const dead = getPhotoDeadLetter().filter(i => i.id !== id);
  localStorage.setItem(PHOTO_DEAD_KEY, JSON.stringify(dead));
}

export function clearPhotoDeadLetter() {
  localStorage.removeItem(PHOTO_DEAD_KEY);
}

// ── Last successful sync timestamp ────────────────────────────────────

export function setLastSuccessfulSync(ts: number = Date.now()) {
  try { localStorage.setItem(LAST_SYNC_KEY, String(ts)); } catch {}
}

export function getLastSuccessfulSync(): number | null {
  try {
    const raw = localStorage.getItem(LAST_SYNC_KEY);
    return raw ? Number(raw) : null;
  } catch { return null; }
}

// ── Local Cache ────────────────────────────────────────────────────────

export function setCachedData<T>(key: string, data: T) {
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify({
      data,
      cachedAt: Date.now(),
    }));
  } catch {
    // localStorage full — silently fail
  }
}

export function getCachedData<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed.data as T;
  } catch {
    return null;
  }
}

export function removeCachedData(key: string) {
  localStorage.removeItem(CACHE_PREFIX + key);
}

/** Clears all cached data for a specific user (call on logout) */
export function clearUserCache(userId: string) {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(CACHE_PREFIX) && key.includes(userId)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(k => localStorage.removeItem(k));
}

// ── Connection State ───────────────────────────────────────────────────

export function isOnline(): boolean {
  return navigator.onLine;
}

// ── Offline-aware Supabase query wrapper ───────────────────────────────

/**
 * Per-query timeout ceilings.
 *
 * If we already have cached data for this key, we can fall back fast
 * (5s) because the user already sees content from cache — the network
 * call is just a refresh. Waiting longer just delays the inevitable
 * fallback.
 *
 * If we have NO cache, we must wait longer (12s) because falling back
 * to "no data" is worse than a small wait that might still succeed.
 *
 * Both cases are well above p99 cellular RTT for a normal query but
 * short enough that 1-2 slow queries don't compound into a 30s+ wait.
 */
const CACHED_QUERY_TIMEOUT_WITH_CACHE_MS = 5_000;
const CACHED_QUERY_TIMEOUT_NO_CACHE_MS = 12_000;

/**
 * Executes a Supabase query and caches the result. If offline, returns cached data.
 * Silently handles errors — never shows error toasts to the user.
 *
 * Behavior:
 *   - Online + fast query    → fresh data, write-through to cache
 *   - Online + query errors  → cached data (silent fallback)
 *   - Online + query hangs   → cached data after timeout; the in-flight
 *                              query keeps running in the background
 *                              and updates the cache for next time
 *   - Offline                → cached data immediately
 */
export async function cachedQuery<T>(
  cacheKey: string,
  queryFn: () => PromiseLike<{ data: T | null; error: any }>
): Promise<{ data: T | null; fromCache: boolean }> {
  if (!navigator.onLine) {
    const cached = getCachedData<T>(cacheKey);
    return { data: cached, fromCache: true };
  }

  // Race the actual query against a timeout. Whichever resolves first
  // wins. We start the query once and hand the same promise to both
  // the race and the background-update closure so the network call
  // is not duplicated.
  //
  // Adaptive timeout: if cache exists, we can fall back fast (5s)
  // because the user sees cached data either way. Without cache,
  // we wait longer (12s) since "no data" is the alternative.
  const hasCache = getCachedData<T>(cacheKey) !== null;
  const timeoutMs = hasCache
    ? CACHED_QUERY_TIMEOUT_WITH_CACHE_MS
    : CACHED_QUERY_TIMEOUT_NO_CACHE_MS;

  const queryPromise = Promise.resolve(queryFn());
  const timeoutPromise = new Promise<{ __timeout: true }>(resolve => {
    setTimeout(() => resolve({ __timeout: true }), timeoutMs);
  });

  try {
    const result = await Promise.race([
      queryPromise.then(r => ({ ...r, __timeout: false as const })),
      timeoutPromise,
    ]);

    if ('__timeout' in result && result.__timeout) {
      const cached = getCachedData<T>(cacheKey);
      // Best-effort: let the original query keep running and update
      // the cache for next time. Swallow any error — we already
      // returned cached data to the caller.
      queryPromise.then(r => {
        if (!r.error && r.data !== null) setCachedData(cacheKey, r.data);
      }).catch(() => {});
      return { data: cached, fromCache: true };
    }

    const { data, error } = result as { data: T | null; error: any };
    if (error) {
      const cached = getCachedData<T>(cacheKey);
      return { data: cached, fromCache: true };
    }
    if (data !== null) {
      setCachedData(cacheKey, data);
    }
    return { data, fromCache: false };
  } catch {
    const cached = getCachedData<T>(cacheKey);
    return { data: cached, fromCache: true };
  }
}

/**
 * Throttle para el toast "guardado offline". Si el usuario marca 10
 * ejercicios sin red, no queremos 10 toasts uno tras otro — solo
 * uno cada N segundos confirmando que todo se está guardando.
 */
let lastQueuedToastAt = 0;
const QUEUED_TOAST_THROTTLE_MS = 4000;

function dispatchQueuedNotification() {
  const now = Date.now();
  if (now - lastQueuedToastAt < QUEUED_TOAST_THROTTLE_MS) return;
  lastQueuedToastAt = now;
  // Listener en useOnlineStatus o similar muestra el toast — lib NO
  // importa sonner directamente para no acoplar la capa de datos
  // a la capa de UI.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('iftmx:mutation-queued'));
  }
}

/**
 * Executes a Supabase mutation. If offline, queues it for later sync.
 * Returns true if executed or queued successfully.
 *
 * The data object should ideally contain a `user_id` field — that field is
 * also captured into the queue item's userId so we only sync items for the
 * currently authenticated user. Falls back to data.client_id, then no userId
 * (legacy items still process for any user).
 *
 * Notifica al usuario cuando una operación se encola offline para que
 * sepa que su acción SÍ se guardó (solo que aún no llegó al servidor).
 * El toast está throttled para no spamear si hace muchas acciones
 * seguidas.
 */
export async function offlineMutation(
  table: string,
  action: 'insert' | 'update' | 'upsert' | 'delete',
  data: Record<string, unknown>,
  mutationFn: () => PromiseLike<{ error: any }>,
  matchColumns?: string[]
): Promise<{ executed: boolean; queued: boolean; error: any }> {
  const userId = (data as any)?.user_id || (data as any)?.client_id || undefined;

  if (!navigator.onLine) {
    addToSyncQueue({ table, action, data, matchColumns, userId });
    dispatchQueuedNotification();
    return { executed: false, queued: true, error: null };
  }

  try {
    const { error } = await mutationFn();
    if (error) {
      // Network might have dropped during the call — queue it
      addToSyncQueue({ table, action, data, matchColumns, userId });
      dispatchQueuedNotification();
      return { executed: false, queued: true, error };
    }
    return { executed: true, queued: false, error: null };
  } catch {
    addToSyncQueue({ table, action, data, matchColumns, userId });
    dispatchQueuedNotification();
    return { executed: false, queued: true, error: null };
  }
}
